import { z } from 'zod';
import { DueSchema, type Due } from './todo.ts';
import {
  AuditSchema,
  DateOnlySchema,
  IdSchema,
  InstantSchema,
  VisibilitySchema,
} from './common.ts';
import { MoneySchema, subtractMoney, zeroMoney, type Money } from '../money.ts';
import { RecordRefSchema } from './notification.ts';

/**
 * An allowance: money set aside for a choice the owner has not made yet.
 *
 * Its own collection rather than a field on the selection, and this is the
 * decision that keeps the whole module readable by an owner. An allowance is
 * a **budget** figure — what the builder has priced the job to absorb — and
 * the owner must never see it. A choice's `price` is what the owner pays.
 * Keeping the two apart means the selection document itself holds nothing
 * confidential, so the owner can read it directly and no mirror is needed.
 *
 * `docs/features/06-selections.md` shows "Remaining from Allowance" on the
 * owner's own page, which looks like a contradiction and is not: the
 * *remaining* figure is computed for internal staff, and the portal shows
 * the price only. If that ever has to change, it is a mirror like the change
 * orders' — not a field hidden by a rule, which cannot be done.
 */
export const AllowanceSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  name: z.string().trim().min(1).max(200),
  amount: MoneySchema,
  costCodeId: IdSchema.optional(),
});
export type Allowance = z.infer<typeof AllowanceSchema>;

/**
 * One option the owner can pick.
 *
 * `price` is the owner's price and the only money here. A choice with no
 * price is a real state — "TBD", the catalogue has not come back yet — and
 * the feature doc has a status for it, so the field is optional rather than
 * defaulted to zero. Zero would be a lie that reconciles.
 */
export const ChoiceSchema = z.object({
  id: IdSchema,
  title: z.string().trim().min(1).max(200),
  description: z.string().max(2_000).optional(),
  price: MoneySchema.optional(),
  vendorContactId: IdSchema.optional(),
  /** A file in the job's Photos folder, so the owner can see the thing. */
  imageFileId: IdSchema.optional(),
});
export type Choice = z.infer<typeof ChoiceSchema>;

/**
 * A selection's deadline, accepting both shapes.
 *
 * The union is tried in order: a string is the pre-P4-19 shape and is
 * lifted to `{kind: 'date'}`; anything else must be a `Due`. The two
 * cannot be confused — one is a string and the other an object — so the
 * coercion is lossless and needs no flag to say which era a document is
 * from.
 *
 * It stays after the migration rather than being removed with it. It costs
 * one `z.union`, and it means a document restored from an old backup reads
 * as a date instead of an error.
 */
export const SelectionDeadlineSchema = z.union([
  DateOnlySchema.transform((date) => ({ kind: 'date' as const, date })),
  DueSchema,
]);

export const SelectionSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  title: z.string().trim().min(1).max(200),
  description: z.string().max(20_000).default(''),
  /** "03 - Doors". A string until there is a category list to point at. */
  category: z.string().trim().max(120).optional(),
  /** "Kitchen", "Master bath". What the list groups by, by default. */
  location: z.string().trim().max(120).optional(),
  allowanceId: IdSchema.optional(),
  choices: z.array(ChoiceSchema).default([]),
  /**
   * Shown to the owner, which is what makes it answerable.
   *
   * A boolean rather than a status, because every other part of the state
   * is derived and a stored status would be the one thing that can disagree
   * with the rest. `selectionStatus` turns these into the labels the
   * feature doc lists.
   */
  released: z.boolean().default(false),
  /**
   * Locked: no longer changeable, because the order has gone in.
   *
   * Separate from "selected" on purpose. An owner changing their mind the
   * day after choosing is ordinary; changing it after the tiles are cut is
   * not, and the difference is a person deciding, not a date.
   */
  locked: z.boolean().default(false),
  /**
   * When an answer is wanted — a date, or a schedule item it follows
   * (P4-19).
   *
   * `docs/features/06-selections.md` §2 offers `Choose Date | Link to
   * Schedule Item`, and §5 makes it an acceptance criterion: a deadline
   * linked to "Cabinet order" −14 days **moves when the item moves**. That
   * is only possible if the link is stored, not a date copied at the time.
   *
   * The union is `DueSchema`, the one a to-do already uses, rather than a
   * second shape meaning the same thing — `resolveDue` then works for both
   * and there is one definition of "two working days before framing".
   *
   * **A bare `YYYY-MM-DD` is still accepted on read**, and becomes
   * `{kind: 'date'}`. Documents written before P4-19 carry the old shape,
   * and a schema that rejected them would turn every selection written
   * before today into a validation error rather than a date.
   */
  deadline: SelectionDeadlineSchema.optional(),
  selectedChoiceId: IdSchema.optional(),
  selectedBy: IdSchema.optional(),
  selectedAt: InstantSchema.optional(),
  visibility: VisibilitySchema,
  tagIds: z.array(IdSchema).default([]),
  /**
   * What this selection is about — a schedule item, mostly.
   *
   * `docs/features/06-selections.md` has "Linked Schedule Item" as a column
   * on the list and "linked schedule items show a warning" when a deadline
   * passes, so the relationship has to be readable from both ends. The
   * **selection owns it** and the schedule item queries for it, exactly as
   * a to-do owns its links: a list on each side is two writes that
   * eventually disagree, and there is no third document to hold the pair.
   *
   * Which end owns it is not arbitrary. A schedule item is written by the
   * publish pipeline and dragged about on a Gantt chart; a selection is
   * edited by a person in a modal. Putting the link on the quieter document
   * means a schedule shift never has to rewrite it.
   */
  linkedRecords: z.array(RecordRefSchema).default([]),
  /** Query form of `linkedRecords`, derived — see `File.linkedKeys`. */
  linkedKeys: z.array(z.string().min(1)).default([]),
});
export type Selection = z.infer<typeof SelectionSchema>;

export const SELECTION_STATUSES = [
  'unreleased',
  'available',
  'tbd',
  'selected',
  'expired',
  'locked',
] as const;
export type SelectionStatus = (typeof SELECTION_STATUSES)[number];

/**
 * Where a selection has got to, derived from what is stored.
 *
 * Order matters and is the whole of the logic. `locked` wins over
 * everything, because a locked selection is finished whatever else is true.
 * `selected` beats `expired`: a deadline that passed after the owner chose
 * is not a problem, and showing one as expired would send somebody chasing
 * an answer they already have. `tbd` beats `available` because a selection
 * with a priceless choice cannot honestly be called available — the owner
 * would be choosing without knowing what it costs.
 */
/**
 * The deadline as a plain date, when it is one.
 *
 * `null` for a deadline that follows a schedule item: resolving that needs
 * the schedule and the job's working calendar, which `resolveDue` does and
 * this deliberately does not. The honest default for a caller that has
 * neither is "not resolved" rather than a guess — an unresolved deadline
 * must not make a selection look expired.
 */
export function selectionDeadlineDate(deadline: Due | undefined): string | null {
  return deadline?.kind === 'date' ? deadline.date : null;
}

/**
 * A deadline as a person reads it.
 *
 * A plain date reads as itself. One that follows a schedule item reads as
 * the resolved date when the caller could resolve it, and as a description
 * of the link when it could not — "follows a schedule item" is honest
 * about an unresolved link, where a blank would look like no deadline at
 * all and a bare date would hide that it moves.
 */
export function describeDeadline(deadline: Due | undefined, resolved?: string | null): string {
  if (!deadline) return '';
  if (deadline.kind === 'date') return deadline.date;
  if (resolved) return resolved;
  const offset = deadline.offsetDays;
  const when =
    offset === 0
      ? `at its ${deadline.anchor}`
      : `${Math.abs(offset)} working day${Math.abs(offset) === 1 ? '' : 's'} ${
          offset < 0 ? 'before' : 'after'
        } its ${deadline.anchor}`;
  return `follows a schedule item, ${when}`;
}

export function selectionStatus(
  selection: Pick<Selection, 'released' | 'locked' | 'selectedChoiceId' | 'choices'>,
  today: string,
  /**
   * The deadline as a plain date, already resolved.
   *
   * Passed in rather than read off the selection, because a deadline that
   * follows a schedule item cannot be resolved without the schedule and
   * the job's working calendar — neither of which belongs in a status
   * function. `isOverdue` on a to-do takes the same shape for the same
   * reason. Absent means "no deadline, or one that cannot be resolved",
   * and both mean the selection is not expired.
   */
  deadline?: string | null,
): SelectionStatus {
  if (selection.locked) return 'locked';
  if (!selection.released) return 'unreleased';
  if (selection.selectedChoiceId !== undefined) return 'selected';
  if (deadline != null && deadline < today) return 'expired';
  if (selection.choices.length === 0) return 'tbd';
  if (selection.choices.some((choice) => choice.price === undefined)) return 'tbd';
  return 'available';
}

/** The labels the feature doc uses, so one place decides the wording. */
export const SELECTION_STATUS_LABEL: Record<SelectionStatus, string> = {
  unreleased: 'Not released',
  available: 'Waiting for a choice',
  tbd: 'Choices to come',
  selected: 'Chosen',
  expired: 'Past due',
  locked: 'Locked',
};

/** The choice the owner picked, if they have. */
export function selectedChoice(selection: Pick<Selection, 'choices' | 'selectedChoiceId'>) {
  if (selection.selectedChoiceId === undefined) return undefined;
  return selection.choices.find((choice) => choice.id === selection.selectedChoiceId);
}

/** What the owner is being charged for this selection, once they choose. */
export function selectionPrice(
  selection: Pick<Selection, 'choices' | 'selectedChoiceId'>,
): Money | undefined {
  return selectedChoice(selection)?.price;
}

/**
 * What is left of the allowance after the choice.
 *
 * Negative when the choice costs more than was set aside, and returned
 * negative rather than clamped: an over-allowance is the figure somebody
 * needs to see, and a zero would hide the overspend until the invoice.
 *
 * Undefined when there is no allowance or no priced choice — which is
 * different from zero, and the difference is "nothing has been decided"
 * against "it came out exactly even".
 */
export function remainingFromAllowance(
  selection: Pick<Selection, 'choices' | 'selectedChoiceId'>,
  allowance: Pick<Allowance, 'amount'> | undefined,
): Money | undefined {
  if (!allowance) return undefined;
  const price = selectionPrice(selection);
  if (!price) return allowance.amount;
  return subtractMoney(allowance.amount, price);
}

/** Whether a selection has overspent its allowance. */
export function isOverAllowance(
  selection: Pick<Selection, 'choices' | 'selectedChoiceId'>,
  allowance: Pick<Allowance, 'amount'> | undefined,
): boolean {
  const remaining = remainingFromAllowance(selection, allowance);
  return remaining !== undefined && remaining.amount < 0;
}

/**
 * Whether the owner may choose right now.
 *
 * Four conditions, and every one of them has a reason: it has to be
 * released (nobody answers a question they were not asked), not locked (the
 * order has gone in), not already chosen by somebody else in the meantime,
 * and the choice has to have a price — an owner picking a priceless option
 * is agreeing to an amount nobody has told them.
 */
export function canChoose(
  selection: Pick<Selection, 'released' | 'locked' | 'selectedChoiceId' | 'choices'>,
  choiceId: string,
): boolean {
  if (!selection.released || selection.locked) return false;
  const choice = selection.choices.find((candidate) => candidate.id === choiceId);
  return choice !== undefined && choice.price !== undefined;
}

export interface AllowanceRollup {
  readonly allowed: Money;
  readonly chosen: Money;
  readonly remaining: Money;
  readonly overspent: number;
}

/**
 * Every allowance against what has been chosen out of it.
 *
 * The figure the office looks at before a budget meeting: not "are we over
 * on the tiles" but "how many of these are over, and by how much in total".
 */
export function rollUpAllowances(
  selections: readonly Selection[],
  allowances: readonly Allowance[],
): AllowanceRollup {
  const currency = allowances[0]?.amount.currency;
  const byId = new Map(allowances.map((allowance) => [allowance.id, allowance] as const));

  let allowed = 0;
  let chosen = 0;
  let overspent = 0;

  for (const allowance of allowances) allowed += allowance.amount.amount;

  for (const selection of selections) {
    const price = selectionPrice(selection);
    if (!price) continue;
    chosen += price.amount;
    const allowance = selection.allowanceId ? byId.get(selection.allowanceId) : undefined;
    if (allowance && price.amount > allowance.amount.amount) overspent += 1;
  }

  const zero = zeroMoney(currency);
  return {
    allowed: { amount: allowed, currency: zero.currency },
    chosen: { amount: chosen, currency: zero.currency },
    remaining: { amount: allowed - chosen, currency: zero.currency },
    overspent,
  };
}
